
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { 
  Building2, User, Mail, Phone, Briefcase, Calendar, MapPin, 
  Monitor, DollarSign, CreditCard, Clock, CheckCircle, XCircle, 
  AlertCircle, Image, FileText, Globe, Archive, Rocket, Eye,
  TrendingUp, Users, Loader2, Trash2
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { AdminCampaign } from "../types";
import { getPaymentTypeText, getPaymentTypeColor, getStatusColor, getStatusText } from "../utils";
import GroupedCreativesDisplay from "@/components/ui/GroupedCreativesDisplay";

interface CampaignDetailsDialogProps {
  campaign: AdminCampaign | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateStatus?: (campaignId: string, newStatus: 'approved' | 'rejected' | 'pending', rejectionReason?: string) => void;
  onDelete?: (campaignId: string) => void;
  updating?: string | null;
}

export const CampaignDetailsDialog = ({ 
  campaign, 
  isOpen, 
  onClose, 
  onUpdateStatus,
  onDelete,
  updating 
}: CampaignDetailsDialogProps) => {
  const { language } = useLanguage();
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionInput, setShowRejectionInput] = useState(false);

  if (!campaign) return null;

  const handleStatusChange = (newStatus: string) => {
    setSelectedStatus(newStatus);
    if (newStatus === 'rejected') {
      setShowRejectionInput(true);
    } else {
      setShowRejectionInput(false);
      setRejectionReason("");
    }
  };

  const handleConfirmStatusChange = () => {
    if (!onUpdateStatus || !selectedStatus) return;
    
    if (selectedStatus === 'rejected' && !rejectionReason.trim()) {
      return;
    }
    
    onUpdateStatus(
      campaign.id, 
      selectedStatus as 'approved' | 'rejected' | 'pending',
      selectedStatus === 'rejected' ? rejectionReason : undefined
    );
    setSelectedStatus("");
    setRejectionReason("");
    setShowRejectionInput(false);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = () => {
    if (!campaign.start_date || !campaign.end_date) return 0;
    const start = new Date(campaign.start_date);
    const end = new Date(campaign.end_date);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  const locations = Array.isArray(campaign.selected_locations) 
    ? campaign.selected_locations 
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                {campaign.brand_name || campaign.company_name || 'Unnamed Campaign'}
                <Badge className={`${getStatusColor(campaign.status)} border-0`}>
                  {getStatusText(campaign.status, language)}
                </Badge>
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                ID: {campaign.id}
              </p>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {/* Status Change Section */}
            {onUpdateStatus && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    {language === 'ar' ? 'تغيير حالة الحملة' : 'Change Campaign Status'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="space-y-2 flex-1 min-w-[200px]">
                      <Label>{language === 'ar' ? 'الحالة الجديدة' : 'New Status'}</Label>
                      <Select value={selectedStatus} onValueChange={handleStatusChange}>
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'ar' ? 'اختر الحالة' : 'Select status'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">
                            <span className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-amber-500" />
                              {language === 'ar' ? 'قيد المراجعة' : 'Pending'}
                            </span>
                          </SelectItem>
                          <SelectItem value="approved">
                            <span className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              {language === 'ar' ? 'موافق عليها' : 'Approved'}
                            </span>
                          </SelectItem>
                          <SelectItem value="rejected">
                            <span className="flex items-center gap-2">
                              <XCircle className="h-4 w-4 text-red-500" />
                              {language === 'ar' ? 'مرفوضة' : 'Rejected'}
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button
                      onClick={handleConfirmStatusChange}
                      disabled={!selectedStatus || (selectedStatus === 'rejected' && !rejectionReason.trim()) || updating === campaign.id}
                      className="min-w-[120px]"
                    >
                      {updating === campaign.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        language === 'ar' ? 'تأكيد التغيير' : 'Confirm Change'
                      )}
                    </Button>
                  </div>
                  
                  {showRejectionInput && (
                    <div className="space-y-2">
                      <Label>{language === 'ar' ? 'سبب الرفض (مطلوب)' : 'Rejection Reason (required)'}</Label>
                      <Textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder={language === 'ar' ? 'أدخل سبب رفض الحملة...' : 'Enter reason for rejection...'}
                        rows={3}
                      />
                    </div>
                  )}
                  
                  {campaign.rejection_reason && campaign.status === 'rejected' && (
                    <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                      <p className="text-sm font-medium text-destructive flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        {language === 'ar' ? 'سبب الرفض الحالي:' : 'Current Rejection Reason:'}
                      </p>
                      <p className="text-sm text-destructive/80 mt-1">{campaign.rejection_reason}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="details">
                  <FileText className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'التفاصيل' : 'Details'}
                </TabsTrigger>
                <TabsTrigger value="locations">
                  <MapPin className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'المواقع' : 'Locations'}
                </TabsTrigger>
                <TabsTrigger value="creatives">
                  <Image className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'الإعلانات' : 'Creatives'}
                </TabsTrigger>
                <TabsTrigger value="performance">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'الأداء' : 'Performance'}
                </TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Company Information */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        {language === 'ar' ? 'معلومات الشركة' : 'Company Information'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <DetailRow 
                        icon={<Building2 className="h-4 w-4" />}
                        label={language === 'ar' ? 'اسم الشركة' : 'Company Name'}
                        value={campaign.company_name}
                      />
                      <DetailRow 
                        icon={<Globe className="h-4 w-4" />}
                        label={language === 'ar' ? 'العلامة التجارية' : 'Brand Name'}
                        value={campaign.brand_name}
                      />
                      <DetailRow 
                        icon={<User className="h-4 w-4" />}
                        label={language === 'ar' ? 'المسؤول' : 'Person in Charge'}
                        value={campaign.person_in_charge}
                      />
                      <DetailRow 
                        icon={<Briefcase className="h-4 w-4" />}
                        label={language === 'ar' ? 'المسمى الوظيفي' : 'Job Title'}
                        value={campaign.job_title}
                      />
                      <DetailRow 
                        icon={<Mail className="h-4 w-4" />}
                        label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                        value={campaign.email}
                        isEmail
                      />
                      <DetailRow 
                        icon={<Phone className="h-4 w-4" />}
                        label={language === 'ar' ? 'الهاتف' : 'Mobile'}
                        value={campaign.mobile}
                      />
                      {campaign.creator_email && (
                        <DetailRow 
                          icon={<Mail className="h-4 w-4" />}
                          label={language === 'ar' ? 'البريد المسجل' : 'Creator Email'}
                          value={campaign.creator_email}
                          isEmail
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* Campaign Timing */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        {language === 'ar' ? 'توقيت الحملة' : 'Campaign Timing'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <DetailRow 
                        icon={<Calendar className="h-4 w-4" />}
                        label={language === 'ar' ? 'تاريخ البدء' : 'Start Date'}
                        value={formatDate(campaign.start_date)}
                      />
                      <DetailRow 
                        icon={<Calendar className="h-4 w-4" />}
                        label={language === 'ar' ? 'تاريخ الانتهاء' : 'End Date'}
                        value={formatDate(campaign.end_date)}
                      />
                      <DetailRow 
                        icon={<Clock className="h-4 w-4" />}
                        label={language === 'ar' ? 'المدة' : 'Duration'}
                        value={`${calculateDuration()} ${language === 'ar' ? 'يوم' : 'days'}`}
                      />
                      <Separator />
                      <DetailRow 
                        icon={<Clock className="h-4 w-4" />}
                        label={language === 'ar' ? 'تاريخ الإنشاء' : 'Created At'}
                        value={formatDateTime(campaign.created_at)}
                      />
                      {campaign.updated_at && (
                        <DetailRow 
                          icon={<Clock className="h-4 w-4" />}
                          label={language === 'ar' ? 'آخر تحديث' : 'Last Updated'}
                          value={formatDateTime(campaign.updated_at)}
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* Financial Information */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-primary" />
                        {language === 'ar' ? 'المعلومات المالية' : 'Financial Information'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <DetailRow 
                        icon={<DollarSign className="h-4 w-4" />}
                        label={language === 'ar' ? 'الميزانية الإجمالية' : 'Total Budget'}
                        value={`${campaign.total_budget?.toLocaleString() || 0} ${language === 'ar' ? 'ريال' : 'SAR'}`}
                        highlight
                      />
                      <DetailRow 
                        icon={<CreditCard className="h-4 w-4" />}
                        label={language === 'ar' ? 'نوع الدفع' : 'Payment Type'}
                        value={
                          <span className={getPaymentTypeColor(campaign.payment_type || 'immediate')}>
                            {getPaymentTypeText(campaign.payment_type || 'immediate', language)}
                          </span>
                        }
                      />
                      {campaign.bank_transfer_status && (
                        <DetailRow 
                          icon={<CreditCard className="h-4 w-4" />}
                          label={language === 'ar' ? 'حالة التحويل البنكي' : 'Bank Transfer Status'}
                          value={campaign.bank_transfer_status}
                        />
                      )}
                      <DetailRow 
                        icon={<CheckCircle className="h-4 w-4" />}
                        label={language === 'ar' ? 'الشروط مقبولة' : 'Terms Accepted'}
                        value={campaign.terms_accepted ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            {language === 'ar' ? 'نعم' : 'Yes'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            {language === 'ar' ? 'لا' : 'No'}
                          </Badge>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* Campaign Status & Meta */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        {language === 'ar' ? 'حالة الحملة' : 'Campaign Status'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <DetailRow 
                        icon={<Monitor className="h-4 w-4" />}
                        label={language === 'ar' ? 'عدد الأجهزة' : 'Total Devices'}
                        value={campaign.total_devices?.toString() || '0'}
                        highlight
                      />
                      {campaign.media_owner_name && (
                        <DetailRow 
                          icon={<Building2 className="h-4 w-4" />}
                          label={language === 'ar' ? 'مالك الوسائط' : 'Media Owner'}
                          value={campaign.media_owner_name}
                        />
                      )}
                      {campaign.screen_types && (
                        <DetailRow 
                          icon={<Monitor className="h-4 w-4" />}
                          label={language === 'ar' ? 'أنواع الشاشات' : 'Screen Types'}
                          value={campaign.screen_types}
                        />
                      )}
                      <DetailRow 
                        icon={<Rocket className="h-4 w-4" />}
                        label={language === 'ar' ? 'تم الإطلاق' : 'Launched'}
                        value={campaign.is_launched ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            {language === 'ar' ? 'نعم' : 'Yes'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            {language === 'ar' ? 'لا' : 'No'}
                          </Badge>
                        )}
                      />
                      <DetailRow 
                        icon={<Archive className="h-4 w-4" />}
                        label={language === 'ar' ? 'مؤرشفة' : 'Archived'}
                        value={campaign.is_archived ? (
                          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">
                            {language === 'ar' ? 'نعم' : 'Yes'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            {language === 'ar' ? 'لا' : 'No'}
                          </Badge>
                        )}
                      />
                      {campaign.user_id && (
                        <DetailRow 
                          icon={<User className="h-4 w-4" />}
                          label={language === 'ar' ? 'معرف المستخدم' : 'User ID'}
                          value={<span className="font-mono text-xs">{campaign.user_id}</span>}
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Locations Tab */}
              <TabsContent value="locations" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      {language === 'ar' ? 'المواقع المختارة' : 'Selected Locations'} ({locations.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {locations.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        {language === 'ar' ? 'لا توجد مواقع محددة' : 'No locations selected'}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {locations.map((location: any, index: number) => (
                          <div key={index} className="p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium">{location.name || `Location ${index + 1}`}</h4>
                                {location.mediaOwner && (
                                  <p className="text-sm text-muted-foreground">
                                    {language === 'ar' ? 'مالك الوسائط:' : 'Media Owner:'} {location.mediaOwner}
                                  </p>
                                )}
                                {location.region && (
                                  <p className="text-sm text-muted-foreground">
                                    {language === 'ar' ? 'المنطقة:' : 'Region:'} {location.region}
                                  </p>
                                )}
                              </div>
                              {location.devices && (
                                <Badge variant="secondary">
                                  {location.devices} {language === 'ar' ? 'شاشة' : 'screens'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Creatives Tab */}
              <TabsContent value="creatives" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Image className="h-4 w-4 text-primary" />
                      {language === 'ar' ? 'الإعلانات الإبداعية' : 'Campaign Creatives'} 
                      ({campaign.creative_urls?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <GroupedCreativesDisplay
                      creativeUrls={campaign.creative_urls || []}
                      creativeMetadata={campaign.creative_metadata}
                      language={language}
                      startDate={campaign.start_date}
                      durationDays={campaign.duration_days || calculateDuration()}
                      showGroupHeaders={true}
                      showScheduledDays={true}
                      showDetailedView={true}
                      selectedLocations={locations}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Performance Tab */}
              <TabsContent value="performance" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      {language === 'ar' ? 'أداء الحملة' : 'Campaign Performance'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <Users className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                        <p className="text-sm text-muted-foreground mb-1">
                          {language === 'ar' ? 'الوصول الفعلي' : 'Actual Reach'}
                        </p>
                        <p className="text-3xl font-bold text-blue-600">
                          {campaign.actual_reach?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="text-center p-6 bg-emerald-50 rounded-lg">
                        <TrendingUp className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
                        <p className="text-sm text-muted-foreground mb-1">
                          {language === 'ar' ? 'التفاعل الفعلي' : 'Actual Engagement'}
                        </p>
                        <p className="text-3xl font-bold text-emerald-600">
                          {campaign.actual_engagement || 0}%
                        </p>
                      </div>
                      <div className="text-center p-6 bg-purple-50 rounded-lg">
                        <DollarSign className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                        <p className="text-sm text-muted-foreground mb-1">
                          {language === 'ar' ? 'الإنفاق الفعلي' : 'Actual Spent'}
                        </p>
                        <p className="text-3xl font-bold text-purple-600">
                          {campaign.actual_spent?.toLocaleString() || 0} {language === 'ar' ? 'ريال' : 'SAR'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Delete Campaign */}
            {onDelete && (
              <div className="pt-4 border-t border-border">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="gap-2" disabled={updating === campaign.id}>
                      <Trash2 className="h-4 w-4" />
                      {language === 'ar' ? 'حذف الحملة' : 'Delete Campaign'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {language === 'ar' ? 'حذف الحملة نهائياً؟' : 'Delete Campaign Permanently?'}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {language === 'ar' 
                          ? 'سيتم حذف هذه الحملة نهائياً ولا يمكن استعادتها. هل أنت متأكد؟'
                          : 'This campaign will be permanently deleted and cannot be recovered. Are you sure?'}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{language === 'ar' ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => { onDelete(campaign.id); onClose(); }}
                      >
                        {language === 'ar' ? 'حذف نهائي' : 'Delete Permanently'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Helper component for detail rows
const DetailRow = ({ 
  icon, 
  label, 
  value, 
  highlight = false,
  isEmail = false 
}: { 
  icon: React.ReactNode; 
  label: string; 
  value: React.ReactNode;
  highlight?: boolean;
  isEmail?: boolean;
}) => (
  <div className="flex items-start gap-3">
    <span className="text-muted-foreground mt-0.5">{icon}</span>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm ${highlight ? 'font-semibold text-primary' : ''} ${isEmail ? 'break-all' : 'truncate'}`}>
        {value || '-'}
      </p>
    </div>
  </div>
);
